import { colours } from "@shared/ui.design-system";
import { ChevronRightIcon } from "@shared/ui.icons";
import { Typography } from "@shared/ui.primitives";
import { clsx } from "clsx";
import NextImage from "next/image";
import React from "react";

import * as styles from "./ShadeSelector.css";

type Props = {
  title: string;
  shades: {
    id: string;
    src: string;
    altText?: string | null;
  }[];
  previewCount?: number;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  dataTestId?: string;
  variant?: "standard" | "compact";
  background?: "secondary.soft" | "secondary.light";
  showShadow?: boolean;
};

/**
 * Displays a clickable shade selector with a preview of color shades.
 * Shows a limited number of shade images with an optional count indicator
 * for additional hidden shades. Includes a chevron icon to indicate it's clickable.
 *
 * Features:
 * - Displays shade preview images in a horizontal layout
 * - Shows additional shade count when there are more shades than preview limit
 * - Clickable button interface with hover/focus states
 * - Responsive design with proper accessibility attributes
 *
 * @param {Props} props - Props for the ShadeSelector component.
 * @param {string} props.title - The title text displayed above the shade previews.
 * @param {Array} props.shades - Array of shade objects containing id, src, and optional alt text.
 * @param {string} props.shades[].id - Unique identifier for each shade.
 * @param {string} props.shades[].src - Image source URL for the shade.
 * @param {string} [props.shades[].altText] - Optional alt text for the shade image (defaults to "shade image").
 * @param {number} [props.previewCount=3] - Maximum number of shade previews to display before showing count.
 * @param {function} props.onClick - Callback function triggered when the selector is clicked, receives click event.
 * @param {string} [props.dataTestId] - Optional test ID for testing purposes.
 * @param {string} [props.variant="standard"] - Size variant that determines layout ("standard" or "compact").
 * @param {string} [props.background="secondary.soft"] - Background color variant ("secondary.soft" or "secondary.light").
 * @param {boolean} [props.showShadow=false] - Whether to show a shadow effect on the container.
 * @returns {JSX.Element|null} The rendered shade selector component or null if no shades provided.
 */
export const ShadeSelector: React.FC<Props> = ({
  title,
  shades,
  previewCount = 3,
  onClick,
  dataTestId,
  variant = "standard",
  background = "secondary.soft",
  showShadow = false
}) => {
  if (shades.length === 0) return null;
  const visibleShadeCount = previewCount ? previewCount : shades.length;

  const itemsToDisplay = shades.slice(0, visibleShadeCount);
  const hiddenItemCount = shades.slice(visibleShadeCount).length;

  const content = (
    <>
      {variant !== "compact" && (
        <Typography variant="smallprint-md">{title}</Typography>
      )}

      <div className={styles.footer}>
        <div className={styles.shadeContainer}>
          {itemsToDisplay.map(({ src, altText, id }, index) => {
            const key = `swatch-${id}-${index}`;
            return (
              <NextImage
                className={styles.image[variant]}
                width={variant === "compact" ? 12 : 20}
                height={variant === "compact" ? 12 : 20}
                key={key}
                alt={altText || "shade image"}
                src={src}
              />
            );
          })}
        </div>
        {hiddenItemCount > 0 && (
          <Typography variant="smallprint-sm-bold">
            +{hiddenItemCount}
          </Typography>
        )}
        {(!onClick || variant !== "compact") && (
          <ChevronRightIcon
            fill={colours.primary.medium}
            className={styles.chevronIcon}
            height={20}
            width={20}
          />
        )}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        className={clsx(
          styles.container,
          styles.containerVariants[background],
          styles.containerSizes[variant],
          showShadow && styles.withShadow
        )}
        data-testid={dataTestId}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={clsx(
        styles.container,
        styles.containerVariants[background],
        styles.containerSizes[variant],
        showShadow && styles.withShadow
      )}
      data-testid={dataTestId}
    >
      {content}
    </div>
  );
};
